import classNames from 'classnames'

import GrowthStatus from '~/components/blocks/GrowthStatus/GrowthStatus'
import type { PortfolioCardVariant } from '~/components/blocks/PortfolioCard/PortfolioCard'
import { PortfolioCardHead } from '~/components/blocks/PortfolioCard/PortfolioCard'
import useMabPostHog, {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '~/hooks/useMabPostHog'
import usePortfolioInfoWindow from '~/hooks/usePortfolioInfoWindow'
import { getStringInitials } from '~/utils/getStringInitials'
import { toURL } from '~/utils/urlResolver'

import ArtworkPrice from '../ArtworkPrice/ArtworkPrice'
import ImagePlaceholder from '../ImagePlaceholder/ImagePlaceholder'
import Link from '../Link/Link'
import SpecificImage from '../SpecificImage/SpecificImage'
import TicketLozenges, {
  TicketLozengeSizes,
} from '../TicketLozenges/TicketLozenges'

import styles from './PortfolioTableArtwork.module.css'

export interface PortfolioTableArtworkProps {
  item: any
  openInfoWindowHandler?: (item: any, variant: any) => void
  variant?: any
  hasHistory?: boolean
  testID?: string
  linkArtwork?: boolean
  useInfoDrawer?: boolean
  separateElements?: boolean
  showTradingFloor?: boolean
  size?: PortfolioCardVariant
  extendedInfo?: boolean
  hideAggr?: boolean
}

interface SeparateElementsReturn {
  image: JSX.Element
  portfolioHead: JSX.Element
}

export default function PortfolioTableArtwork({
  item,
  openInfoWindowHandler,
  variant,
  hasHistory,
  testID,
  linkArtwork = false,
  useInfoDrawer = false,
  separateElements = false,
  showTradingFloor = false,
  size = 'default',
  extendedInfo = false,
  hideAggr = false,
}: PortfolioTableArtworkProps): any {
  const { pushEvent } = useMabPostHog()
  const { openInfoWindow } = usePortfolioInfoWindow()

  if (!item) return null

  const { image, title, format, signed, artist, imageUrl } = item.artwork
    ? item.artwork
    : item

  const artworkClickHandler = () => {
    if (linkArtwork) {
      if (useInfoDrawer) openInfoWindow(item, variant, undefined, false)

      pushEvent('Clicked View Artwork', {
        webProduct: PushWebProduct.MyPortfoio,
        interactionType: PushInteractionType.Click,
        interactionLocation: PushInteractionLocation.MyPortfolioPage,
      })
    }
    return
  }

  const renderImage = (size?: string) => (
    <div
      className={classNames(styles.imageContainer, {
        [styles['medium']]: size === 'medium',
      })}
    >
      <div
        className={classNames(
          styles.imageContainerInner,
          styles.imageContainerLinked
        )}
      >
        {(image && image.asset) || imageUrl ? (
          <SpecificImage
            asset={imageUrl ? imageUrl : image.asset}
            alt={`${title} - ${format}`}
            width={106}
          />
        ) : (
          <ImagePlaceholder text={getStringInitials(artist)} />
        )}
      </div>
    </div>
  )

  const renderPortfolioHead = () => (
    <div className={styles.portfolioHead}>
      <PortfolioCardHead
        title={title}
        signed={signed}
        artist={artist}
        format={format}
        variant={
          size && !extendedInfo ? size : extendedInfo ? 'large' : 'default'
        }
      />
      {showTradingFloor && (
        <div className={styles.tradingFloor}>
          <TicketLozenges
            buyTicketsCount={item?.buyTicketsCount}
            sellTicketsCount={item?.sellTicketsCount}
            ownedTicketsCount={item?.ownedTicketsCount}
            size={TicketLozengeSizes.small}
            inline
            paddingTop={false}
          />
        </div>
      )}
      {extendedInfo && (
        <div className={styles.extendedFooter}>
          <ArtworkPrice
            listPriceMin={item.listPriceMin}
            listPriceMax={item.listPriceMax}
            listPriceMinValues={item.listPriceMinValues}
            listPriceMaxValues={item.listPriceMaxValues}
            poa={item.poa}
            smallFont
          />
          {item?.aagr && !hideAggr ? (
            <div className={styles.aagr}>
              <GrowthStatus
                size="medium"
                growth={+item.aagr}
                title="AAGR (5YR)"
                hideGraph
                smallFont
              />
            </div>
          ) : (
            ''
          )}
        </div>
      )}
    </div>
  )

  // Return separate elements if requested
  if (separateElements) {
    return {
      image: renderImage(size),
      portfolioHead: renderPortfolioHead(),
    }
  }

  const renderInner = () => {
    return (
      <div
        data-testid={testID}
        className={classNames(styles.artworkContainer, {
          [styles.hasLink]: linkArtwork,
          [styles[size]]: size,
        })}
        onClick={artworkClickHandler}
      >
        {renderImage()}
        {renderPortfolioHead()}
      </div>
    )
  }

  return (
    <>
      {!useInfoDrawer && linkArtwork ? (
        <Link
          href={toURL({
            slug: item.artworkSlug,
            artist: item.artistSlug,
            collection: item.collectionSlug,
            collectionTaxonomy: item.collectionTaxonomy,
            _type: 'artwork',
          })}
        >
          {renderInner()}
        </Link>
      ) : (
        renderInner()
      )}
    </>
  )
}
